import { createElement } from 'react';

// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid';

import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Decoration, type DecorationSet } from '@atlaskit/editor-prosemirror/view';

import {
	CURSOR_DROP_INDICATOR_GAP,
	CURSOR_DROP_INDICATOR_OFFSET,
	CURSOR_DROP_INDICATOR_WIDTH,
	CursorDropIndicator,
} from '../ui/cursor-drop-indicator';

export const CURSOR_INDICATOR_DEC_TYPE = 'hub-external-insert-cursor';

export function findCursorIndicatorDecs(decorations: DecorationSet): Decoration[] {
	return decorations.find(0, 1e9, (spec) => spec.type === CURSOR_INDICATOR_DEC_TYPE);
}

/** Minimal gap/offset for cursor indicator alignment (first version – constants). */
function getGapAndOffset(
	_prevNode?: PMNode | null,
	_nextNode?: PMNode | null,
	_parentNode?: PMNode | null,
): { gap: number; offset: number } {
	if (!_prevNode && _nextNode) {
		return { gap: 0, offset: 0 };
	}
	if (_prevNode && !_nextNode) {
		return { gap: 0, offset: 0 };
	}
	return { gap: 4, offset: 0 };
}

const DEFAULT_DROP_INDICATOR_WIDTH = 760;

/**
 * Creates a single widget decoration for the cursor drop indicator at the given position.
 * Renders CursorDropIndicator via the editor portal. lineLength from api.width.sharedState is set on the wrapper.
 */
export function createCursorIndicatorDecoration(
	pos: number,
	nodeViewPortalProviderAPI: PortalProviderAPI,
	prevNode?: PMNode | null,
	nextNode?: PMNode | null,
	parentNode?: PMNode | null,
	lineLength?: number,
): Decoration {
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const key = `hub-external-insert-cursor-${uuid()}`;
	const { gap, offset } = getGapAndOffset(prevNode, nextNode, parentNode);
	const contentWidthPx = `${lineLength ?? DEFAULT_DROP_INDICATOR_WIDTH}px`;

	return Decoration.widget(
		pos,
		() => {
			const element = document.createElement('div');
			element.setAttribute('data-hub-external-insert-cursor', 'true');
			element.style.clear = 'unset';
			element.style.display = 'block';
			element.style.setProperty(CURSOR_DROP_INDICATOR_OFFSET, `${offset}px`);
			element.style.setProperty(CURSOR_DROP_INDICATOR_GAP, `${gap}px`);
			element.style.setProperty(CURSOR_DROP_INDICATOR_WIDTH, contentWidthPx);

			try {
				nodeViewPortalProviderAPI.render(
					() => createElement(CursorDropIndicator),
					element,
					key,
					undefined,
					true,
				);
			} catch {}

			return element;
		},
		{
			type: CURSOR_INDICATOR_DEC_TYPE,
			destroy: () => {
				nodeViewPortalProviderAPI.remove(key);
			},
		},
	);
}
